import { debug } from "debug";
import type { DocumentInitParameters } from "pdfjs-dist/types/src/display/api";

const log = debug("studio:pdfjs");

declare global {
  interface Map<K, V> {
    getOrInsertComputed(key: K, callbackFn: (key: K) => V): V;
  }
}

// Polyfill Map.prototype.getOrInsertComputed (TC39 Stage 3, Chrome 134+)
// Required by pdfjs-dist 5.x.
if (!Map.prototype.getOrInsertComputed) {
  Object.defineProperty(Map.prototype, "getOrInsertComputed", {
    value: function (key: unknown, callbackFn: (key: unknown) => unknown) {
      if (this.has(key)) return this.get(key);
      const value = callbackFn(key);
      this.set(key, value);
      return value;
    },
    writable: true,
    configurable: true,
    enumerable: false,
  });
}

// Lazy-load pdfjs-dist so the polyfill above is guaranteed to run first.
let _pdfjsLib: typeof import("pdfjs-dist") | null = null;

export async function getPdfjsLib() {
  if (!_pdfjsLib) {
    _pdfjsLib = await import("pdfjs-dist");
    const workerSrc = (await import("pdfjs-dist/build/pdf.worker.min.mjs?url"))
      .default;
    _pdfjsLib.GlobalWorkerOptions.workerSrc = workerSrc;
    log("loaded pdfjs-dist, workerSrc: %s", workerSrc);
  }
  return _pdfjsLib;
}

/**
 * Opens a document with the decoder wasm pdfjs needs for JPEG2000 and JBIG2
 * images. Load through here rather than calling `getDocument` directly: without
 * `wasmUrl` those images silently drop out of the rendered page.
 *
 * The trailing slash is pdfjs's: it concatenates filenames onto this.
 */
export async function loadPdfDocument(params: DocumentInitParameters) {
  const pdfjsLib = await getPdfjsLib();
  return pdfjsLib.getDocument({
    ...params,
    wasmUrl: `${import.meta.env.BASE_URL}pdfjs/`,
  });
}
